import { DataItem, HCostData, PROGRESS_STATUS } from "@constants/inbound";
import { useAtom } from "jotai";
import { hcostInboundAtom, hcostInboundDesignsValueAtom } from "@/store/hcost-atom";
import { CheckBox, SelectBox, ScrollView, TextArea } from "devextreme-react";
import { DataGrid, Editing, Column, Item, Toolbar } from "devextreme-react/data-grid";
import React, { useRef, useEffect, useState } from "react";
import { CommonConst } from "@constants/common-const";
import { cloneDeep } from "lodash-es";
import { useRouter } from "next/router";
import { ROUTE } from "@constants/route";
import { userAtom } from "@/store/global";
import Radio from "@components/common/radio";
import jwtService from "@service/jwt-service";
import AlertModal, { alertModalType } from "@components/common/alert-modal";
import {
  _getCounselFileUrl,
  _getCounselPresignedurlFileUrl,
  downloadCounselFile,
  getHCostResultInbound,
  saveHCostInbound,
} from "@service/hcost/hcost-result-service";
import FileUpload from "@feature/counsel/file-upload";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { DesignCellComponent } from "./counsel-design-cell";
import { DesignEditComponent } from "@feature/counsel/update/counsel-design-edit";
import useCounselHook from "@feature/counsel/useCounselHook";
import CounselCompany from "@feature/counsel/update/counsel-company";
import { commonNotify } from "@utils/common";
import CounselBrokerage from "@feature/brokerage/counsel-brokerage";
import { produce } from "immer";

interface Props {
  accessType: string;
  refreshDataSource: Function;
  isBrokerage?: boolean;
}

const CounselCommon = ({ accessType, refreshDataSource, isBrokerage }: Props) => {
  const router = useRouter();
  const [hCostResult, setHCostResult] = useAtom(hcostInboundAtom);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [hcostDesigns, setHcostDesigns] = useAtom(hcostInboundDesignsValueAtom);
  const [userInfo] = useAtom(userAtom);

  const [alertModalVisible, setAlertModalVisible] = useState(false);
  const [alertType, setAlertType] = useState<alertModalType>(null);

  const fileUploader = useRef<HTMLInputElement>();
  const dataGridRef = useRef<DataGrid>();

  const { onValueChangeCounselHandler } = useCounselHook();

  useEffect(() => {
    setAccessToken(jwtService.getToken());
    setRefreshToken(jwtService.getRefreshToken());
  }, [router]);

  useEffect(() => {
    setHcostDesigns(cloneDeep(hCostResult.designs));
  }, [hCostResult.designs, setHcostDesigns]);

  const handleStatusChanged = (e: any) => {
    const { value, name }: { value: string; name: "progressStatus" | "reasonForDiscontinuation" | "conBrokerage" } =
      e.target;

    if (name === "conBrokerage") {
      setHCostResult(prev => ({
        ...prev,
        [name]: prev[name] !== JSON.parse(value) ? JSON.parse(value) : null,
      }));
    } else {
      setHCostResult(prev => ({
        ...prev,
        [name]: prev[name]?.code !== value ? HCostData[name].items.find((val: DataItem) => val.code === value) : null,
      }));
    }
  };
  const alertCallback = (isConfirmed: boolean) => {
    if (isConfirmed) setAlertModalVisible(false);
  };

  const designWidgetOptions = {
    text: "상담 정보로 설계하기",
    type: "default",
    onClick: async () => {
      try {
        let resultId = await saveHCostInbound(CommonConst.MODIFY_TYPE_INBOUND_UPDATE, { ...hCostResult }, accessType);
        let resultData = await getHCostResultInbound(resultId, accessType);

        if (resultData.counselDesigns[0].designTemplate) {
          await toDesign(resultData.counselDesigns[0].designTemplate.designCode);
        } else {
          setAlertModalVisible(true);
          setAlertType("nullDesignTemplateId");
        }
        refreshDataSource();
      } catch (e) {
        console.log(e);
      }
    },
  };

  const routerChangeWidgetOptions = {
    text: "신규",
    icon: "plus",
    onClick: () => {
      window.open(ROUTE.DESIGN_CREATE);
    },
  };

  const addRowWidgetOptions = {
    text: "기존 설계 연결",
    onClick: () => {
      void dataGridRef.current.instance.addRow();
    },
  };

  function setStateValue(rowData: any, value: any) {
    if (value) {
      rowData.id = value.id;
      rowData.title = value.title;
      rowData.customerName = value.customer ? value.customer.name : null;
      rowData.customerMobileNumber = value.customer ? value.customer.mobileNumber : null;
      rowData.companyId = userInfo.companyId;
    }
  }

  const beforeSave = (e: any) => {
    if (e.changes.length && e.changes[0].type === "insert") {
      const changeData = e.changes[0].data;
      const isExist = hcostDesigns.find(v => v.id === changeData.id);

      if (isExist) {
        e.cancel = true;
        commonNotify("이미 등록된 설계 입니다.", "error");
      }
    }
  };

  const progressStatusDescription = HCostData.progressStatus.items.find(
    (v: DataItem) => v.code === hCostResult.progressStatus?.code,
  )?.description;

  const resonForDiscontinuationDescription = HCostData.reasonForDiscontinuation.items.find(
    (v: DataItem) => v.code === hCostResult.reasonForDiscontinuation?.code,
  )?.description;

  const toDesign = async (designCode: string) => {
    if (accessType === CommonConst.ACCESS_TYPE_ADMIN) {
      open(
        `${process.env.NEXT_PUBLIC_VUE_BASE_URL}/login?accessToken=${accessToken}&refreshToken=${refreshToken}&url=admin/company/${hCostResult.company.id}/design/create/${designCode}&counselId=${hCostResult.id}`,
      );
    } else {
      open(
        `${process.env.NEXT_PUBLIC_VUE_BASE_URL}/login?accessToken=${accessToken}&refreshToken=${refreshToken}&url=design/create/${designCode}&counselId=${hCostResult.id}`,
      );
    }
  };
  const getUploadUrl = async (e: any) => {
    if (e.target.files.length < 11) {
      for (let i: number = 0; i < e.target.files.length; i++) {
        // presingedURL
        const newModelData = await _getCounselFileUrl(
          accessType,
          hCostResult.id,
          "hcost-counsel-attached-file",
          e.target.files[i].name,
        );
        setHCostResult(prev => {
          prev.counselAttachedFiles.push({
            id: null,
            fileName: e.target.files[i].name,
            fileUrl: newModelData[0].cdnUrl,
            fileExtension: e.target.files[i].name.split(".").slice(-1)[0],
            fileSize: e.target.files[i].size,
            filePresignedUrl: newModelData[0].presignedUrl,
            file: e.target.files[i],
          });
        });
      }
    } else {
      fileUploader.current.value = null;
      commonNotify(CommonConst.errorMaxFile, "error");
    }
  };

  const handleDelete = async (e: React.MouseEvent<SVGSVGElement, MouseEvent>, idx: number) => {
    setHCostResult(prev => {
      prev.counselAttachedFiles = produce(hCostResult.counselAttachedFiles, draft => {
        draft.splice(idx, 1);
      });
    });
  };

  const downloadFile = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    id: number,
    type: string,
    fileName: string,
  ) => {
    if (id) {
      const file = await _getCounselPresignedurlFileUrl(accessType, hCostResult.id, id);
      await downloadCounselFile(file.filePresignedUrl, type, fileName);
    } else {
    }
  };

  return (
    <ScrollView showScrollbar="always" className="basis-1/2 ">
      <div className="card-body w-full">
        {/*상담 내용*/}
        <div className="content-fieldset">
          <h3 className="content-fieldset-title">견적 상담</h3>
          <div className="content-fieldset-title-desc" />

          <div className="content-field row ">
            <div className="content-field-label">
              <div className="content-label-name">진행 상태</div>
              <div className="content-label-desc" />
            </div>
            <div className="content-field-value full">
              <div className="flex flex-wrap gap-y-2 gap-x-3 mb-2">
                {HCostData.progressStatus.items.map((v: DataItem) => {
                  if (v.code === PROGRESS_STATUS.PREPARATION && accessType !== CommonConst.ACCESS_TYPE_ADMIN) {
                    return null;
                  } else {
                    return (
                      <Radio
                        labelClassName="!text-sm"
                        key={v.value}
                        label={v.value}
                        value={v.code}
                        name={"progressStatus"}
                        onClick={handleStatusChanged}
                        checked={hCostResult.progressStatus?.code === v.code}
                        readOnly={true}
                      ></Radio>
                    );
                  }
                })}
              </div>

              <div className="content-value-desc">{progressStatusDescription || ""}</div>
            </div>
          </div>

          {hCostResult.progressStatus?.code === PROGRESS_STATUS.CONSULTATION_STOP && (
            <div className="content-field row ">
              <div className="content-field-label">
                <div className="content-label-name">종료 사유</div>
                <div className="content-label-desc" />
              </div>
              <div className="content-field-value full">
                <SelectBox
                  dataSource={HCostData.reasonForDiscontinuation.items}
                  value={hCostResult.reasonForDiscontinuation?.code}
                  displayExpr="text"
                  showClearButton={true}
                  placeholder="종료 사유를 선택하세요"
                  valueExpr="code"
                  onValueChanged={onValueChangeCounselHandler((draft, value) => {
                    draft.reasonForDiscontinuation = value ? { code: value } : null;
                  })}
                />

                <div className="content-value-desc">{resonForDiscontinuationDescription || ""}</div>
              </div>
            </div>
          )}

          <div className="content-field row">
            <div className="content-field-label">
              <div className="content-label-name">설계 목록</div>
              <div className="content-label-desc" />
            </div>
            <div className="content-field-value full">
              <DataGrid dataSource={hcostDesigns} ref={dataGridRef} onSaving={beforeSave} showColumnHeaders={false}>
                <Editing
                  mode="row"
                  allowAdding={true}
                  allowDeleting={true}
                  useIcons={true}
                  allowUpdating={true}
                ></Editing>
                <Column
                  alignment="left"
                  caption="설계 번호, 설계 제목, 고객명, 휴대폰번호"
                  cellComponent={e => DesignCellComponent(e, accessType)}
                  editCellComponent={e => DesignEditComponent(e, dataGridRef, accessType, hCostResult.company.id)}
                  setCellValue={setStateValue}
                  name="designs"
                ></Column>
                <Toolbar>
                  <Item widget="dxButton" options={designWidgetOptions} location="after" />
                  <Item widget="dxButton" options={routerChangeWidgetOptions} location="after" />
                  <Item widget="dxButton" options={addRowWidgetOptions} location="after" />
                </Toolbar>
              </DataGrid>
              <div className="content-value-desc">
                {accessType === CommonConst.ACCESS_TYPE_ADMIN
                  ? "※ 조직 변경 시 할당된 설계의 고객, 담당자, 조직정보가 함께 변경됩니다."
                  : ""}
              </div>
            </div>
          </div>

          <div className="content-field row">
            <div className="content-field-label">
              <div className="content-label-name">상담내용(비공개)</div>
              <div className="content-label-desc" />
            </div>
            <div className="content-field-value full">
              <TextArea
                value={hCostResult.counselContentsInternal}
                height={220}
                valueChangeEvent="keyup"
                onValueChanged={onValueChangeCounselHandler((draft, value) => {
                  draft.counselContentsInternal = value;
                })}
              />
              <div className="content-value-desc" />
            </div>
          </div>

          <div className="content-field row">
            <div className="content-field-label">
              <div className="content-label-name">상담내용(공개)</div>
              <div className="content-label-desc" />
            </div>
            <div className="content-field-value full">
              <TextArea
                value={hCostResult.counselContentsPublic}
                height={220}
                valueChangeEvent="keyup"
                onValueChanged={onValueChangeCounselHandler((draft, value) => {
                  draft.counselContentsPublic = value;
                })}
              />
              <div className="content-value-desc" />
            </div>
          </div>

          <div className="content-field row">
            <div className="content-field-label">
              <div className="content-label-name">첨부 파일</div>
              <div className="content-label-desc" />
            </div>
            <div className="content-field-value full">
              <FileUpload accept="*" getUploadUrl={getUploadUrl} fileUploader={fileUploader} buttonName="파일 추가" />
              {hCostResult.counselAttachedFiles.map((v: CounselAttachedFile, i: number) => (
                <div key={v.fileName} className="flex mt-2">
                  <a
                    className={v.id ? "cursor-pointer hover:underline text-[#007bff]" : ""}
                    onClick={e => downloadFile(e, v.id, v.fileExtension, v.fileName)}
                  >
                    {v.fileName}
                  </a>
                  <XCircleIcon className="w-5 ml-2" onClick={e => handleDelete(e, i)} />
                </div>
              ))}
              <div className="content-value-desc" />
            </div>
          </div>
        </div>

        {/*상담 담당*/}
        <CounselCompany accessType={accessType} isBrokerage={isBrokerage} />

        {/*중개 정보*/}
        {isBrokerage && hCostResult.isDefault && <CounselBrokerage accessType={accessType} />}
      </div>
      <AlertModal
        visible={alertModalVisible}
        modalMode={"onlyConfirm"}
        type={alertType}
        alertCallback={e => alertCallback(e)}
      />
    </ScrollView>
  );
};

export default CounselCommon;
